import Image from 'next/image'
import { TEMPLATE_OPTION, TEMPLATES } from '@/constants/offer-template'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import { AutosizeTextarea } from '@/components/ui/textarea'
import { DESCRIPTION_PLACEHOLDER } from './schemas'
import type { DecidedSchema } from './schemas'
import type { UseFormReturn } from 'react-hook-form'
import { TTemplate } from '@/interfaces/offer'

type Props = {
  form: UseFormReturn<DecidedSchema>
  onSubmit: (values: DecidedSchema) => Promise<void> | void
  selectedTemplate: TTemplate | null
  onTemplateChange: (template: TTemplate) => void
}

export function DecidedForm({
  form,
  onSubmit,
  selectedTemplate,
  onTemplateChange,
}: Props) {
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>ご相談内容</FormLabel>
              <FormControl>
                <Input
                  placeholder="ミックス・マスタリングを２曲お願いします"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>内容詳細</FormLabel>
              <div className="md:flex md:items-center md:justify-between">
                <FormDescription className="mb-4">
                  ご相談内容を詳しく記入してください。
                </FormDescription>
                <div className="mb-4 flex items-center justify-end gap-2">
                  <FormControl>
                    <Select
                      value={selectedTemplate || ''}
                      onValueChange={(value: TTemplate) => {
                        onTemplateChange(value)
                        field.onChange(TEMPLATES[value] || '')
                      }}
                    >
                      <SelectTrigger className="w-fit px-2 py-1">
                        <div className="flex items-center gap-1">
                          <Image
                            src="/images/icons/add-template.svg"
                            width="20"
                            height="20"
                            alt="テンプレート"
                          />
                          <div className="text-sm font-medium text-primary">
                            テンプレートを利用する
                          </div>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={TTemplate.SONG_WRITER}>
                          {TEMPLATE_OPTION[TTemplate.SONG_WRITER]}
                        </SelectItem>
                        <SelectItem value={TTemplate.LYRICIST}>
                          {TEMPLATE_OPTION[TTemplate.LYRICIST]}
                        </SelectItem>
                        <SelectItem value={TTemplate.ARRANGER}>
                          {TEMPLATE_OPTION[TTemplate.ARRANGER]}
                        </SelectItem>
                        <SelectItem value={TTemplate.MUSICIAN} disabled>
                          {TEMPLATE_OPTION[TTemplate.MUSICIAN]}
                        </SelectItem>
                        <SelectItem value={TTemplate.RECORDING_STUDIO} disabled>
                          {TEMPLATE_OPTION[TTemplate.RECORDING_STUDIO]}
                        </SelectItem>
                        <SelectItem value={TTemplate.MIX_ENGINEER}>
                          {TEMPLATE_OPTION[TTemplate.MIX_ENGINEER]}
                        </SelectItem>
                        <SelectItem value={TTemplate.MASTERING_ENGINEER}>
                          {TEMPLATE_OPTION[TTemplate.MASTERING_ENGINEER]}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </div>
              </div>
              <FormControl>
                <AutosizeTextarea
                  className="min-h-50"
                  maxHeight={250}
                  minHeight={100}
                  placeholder={DESCRIPTION_PLACEHOLDER}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  )
}
